import { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateSuggestedDesks,
  useExistingSubdesks,
  type DeskCreateResult,
  type SuggestedDeskSubmission,
} from "@/hooks/use-desk-suggestions";
import {
  findExistingDesk,
  findExistingSubdesk,
  sameName,
  type ExistingDesk,
} from "@/lib/desk-suggestions/existing-desks";
import {
  deskValidationError,
  newDeskRequest,
  plural,
  selectionSummary,
  subdeskValidationError,
  syncDeskRequest,
  type SelectionCounts,
} from "@/lib/desk-suggestions/provisioning";
import type {
  DeskSuggestionRun,
  ProvisionOutcome,
  SuggestedDesk,
  SuggestedSubdesk,
} from "@/lib/types/desk-suggestions";

interface DeskSuggestionReviewProps {
  tenantId: string;
  run: DeskSuggestionRun;
  existingDesks: ExistingDesk[];
  existingDesksLoading?: boolean;
  existingDesksError?: boolean;
}

interface SubdeskRow {
  /** `${deskIndex}:${subdeskIndex}`, stable for the run. */
  key: string;
  subdesk: SuggestedSubdesk;
  /** Already in the existing desk, so it is never sent. */
  exists: boolean;
  /** Created by a submit on this page. */
  createdHere: boolean;
}

interface Row {
  index: number;
  desk: SuggestedDesk;
  /** The tenant desk the suggestion already exists as: rows sync into it. */
  existing: ExistingDesk | undefined;
  /** The desk was created by a submit on this page. */
  createdHere: boolean;
  /** Whether the existing desk's subdesks are known yet. */
  subdesksStatus: "ready" | "loading" | "error";
  retrySubdesks: () => void;
  subdesks: SubdeskRow[];
}

const SUBDESK_OUTCOME_LABEL: Record<ProvisionOutcome, string> = {
  created: "created",
  existing: "already existed",
  failed: "failed",
};

function ResultCell({ result }: { result: DeskCreateResult }) {
  return (
    <div className="space-y-1">
      {result.outcome === "created" && result.synced && (
        <>
          <Badge variant="warning">
            <AlertTriangle className="mr-1 h-3 w-3" />
            Created a new desk
          </Badge>
          <p className="text-xs text-amber-700 dark:text-amber-400">
            The existing desk was not found anymore, so a new desk was created
            instead of adding subdesks. Check it in the tenant.
          </p>
        </>
      )}
      {result.outcome === "created" && !result.synced && (
        <Badge variant="positive">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Created
        </Badge>
      )}
      {result.outcome === "existing" && (
        <Badge variant="secondary">Existing desk</Badge>
      )}
      {result.outcome === "failed" && (
        <Badge variant="destructive">
          <XCircle className="mr-1 h-3 w-3" />
          Failed
        </Badge>
      )}
      {result.error && (
        <p className="text-destructive text-xs">{result.error}</p>
      )}
      {result.subdesks.length > 0 && (
        <ul className="space-y-0.5 text-xs">
          {result.subdesks.map((subdesk) => (
            <li
              key={subdesk.name}
              className={
                subdesk.outcome === "failed"
                  ? "text-destructive"
                  : subdesk.outcome === "created"
                    ? "text-green-700 dark:text-green-500"
                    : "text-muted-foreground"
              }
            >
              Subdesk “{subdesk.name}” {SUBDESK_OUTCOME_LABEL[subdesk.outcome]}
              {subdesk.error ? `: ${subdesk.error}` : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DeskSuggestionReview({
  tenantId,
  run,
  existingDesks,
  existingDesksLoading = false,
  existingDesksError = false,
}: DeskSuggestionReviewProps) {
  const [results, setResults] = useState<Map<number, DeskCreateResult>>(
    () => new Map(),
  );

  // A desk created (or matched) by an earlier submit counts as existing even
  // before the tenant's desk list refetches.
  const matched = useMemo(
    () =>
      (run.desks ?? []).map((desk, index) => {
        const result = results.get(index);
        const existing: ExistingDesk | undefined =
          findExistingDesk(desk, existingDesks) ?? result?.desk ?? undefined;
        return { index, desk, existing, result };
      }),
    [run.desks, existingDesks, results],
  );
  const existingDeskIds = useMemo(
    () => [
      ...new Set(
        matched.flatMap(({ existing }) => (existing ? [existing.id] : [])),
      ),
    ],
    [matched],
  );
  const subdeskQueries = useExistingSubdesks(tenantId, existingDeskIds);

  const rows: Row[] = matched.map(({ index, desk, existing, result }) => {
    const createdHere = result?.outcome === "created";
    const query = existing
      ? subdeskQueries[existingDeskIds.indexOf(existing.id)]
      : undefined;
    const subdesksStatus: Row["subdesksStatus"] =
      !existing || query?.data ? "ready" : query?.isError ? "error" : "loading";
    return {
      index,
      desk,
      existing,
      createdHere,
      subdesksStatus,
      retrySubdesks: () => {
        void query?.refetch();
      },
      subdesks: desk.subdesks.map((subdesk, subIndex) => {
        const inDesk =
          query?.data !== undefined &&
          findExistingSubdesk(subdesk.name, query.data) !== undefined;
        const provisioned = result?.subdesks.find(
          (s) => s.outcome !== "failed" && sameName(s.name, subdesk.name),
        );
        return {
          key: `${index}:${subIndex}`,
          subdesk,
          exists:
            existing !== undefined && (inDesk || provisioned !== undefined),
          createdHere: provisioned?.outcome === "created",
        };
      }),
    };
  });

  const [deskChecked, setDeskChecked] = useState<Map<number, boolean>>(
    () => new Map(),
  );
  const [uncheckedSubdesks, setUncheckedSubdesks] = useState<Set<string>>(
    () => new Set(),
  );
  const [descriptions, setDescriptions] = useState<Map<number, string>>(
    () => new Map(),
  );
  const [subdeskDescriptions, setSubdeskDescriptions] = useState<
    Map<string, string>
  >(() => new Map());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [markAppliedError, setMarkAppliedError] = useState<string | null>(null);
  const createDesks = useCreateSuggestedDesks();

  const isNew = (row: Row) => row.existing === undefined;
  const descriptionOf = (row: Row) =>
    descriptions.get(row.index) ?? row.desk.description;
  const subdeskDescriptionOf = (subdesk: SubdeskRow) =>
    subdeskDescriptions.get(subdesk.key) ?? subdesk.subdesk.description;
  const missingSubdesks = (row: Row) => row.subdesks.filter((s) => !s.exists);
  const isDisabledDesk = (row: Row) => row.existing?.enabled === false;
  /**
   * New desks are always selectable; existing ones only when enabled and with
   * a subdesk to add.
   */
  const canSelect = (row: Row) =>
    isNew(row) ||
    (!isDisabledDesk(row) &&
      row.subdesksStatus === "ready" &&
      missingSubdesks(row).length > 0);
  /** New desks start selected; existing desks start unselected. */
  const isChecked = (row: Row) =>
    canSelect(row) && (deskChecked.get(row.index) ?? isNew(row));
  const selectedSubdesks = (row: Row) =>
    isChecked(row)
      ? missingSubdesks(row).filter((s) => !uncheckedSubdesks.has(s.key))
      : [];
  const isSendable = (row: Row) =>
    isChecked(row) && (isNew(row) || selectedSubdesks(row).length > 0);
  const rowError = (row: Row): string | null => {
    if (isNew(row)) {
      const deskError = deskValidationError(row.desk, descriptionOf(row));
      if (deskError) return deskError;
    }
    const invalid = selectedSubdesks(row).find(
      (s) =>
        subdeskValidationError(s.subdesk, subdeskDescriptionOf(s)) !== null,
    );
    return invalid ? `Subdesk “${invalid.subdesk.name}” is invalid.` : null;
  };

  const sendableRows = rows.filter(isSendable);
  const invalidRows = sendableRows.filter((row) => rowError(row) !== null);
  const counts: SelectionCounts = {
    newDesks: 0,
    newDeskSubdesks: 0,
    existingDesks: 0,
    existingDeskSubdesks: 0,
  };
  for (const row of sendableRows) {
    if (isNew(row)) {
      counts.newDesks += 1;
      counts.newDeskSubdesks += selectedSubdesks(row).length;
    } else {
      counts.existingDesks += 1;
      counts.existingDeskSubdesks += selectedSubdesks(row).length;
    }
  }
  const summary = selectionSummary(counts);

  const runAcceptsCreation =
    run.status === "completed" || run.status === "applied";
  const canCreate =
    runAcceptsCreation &&
    !existingDesksLoading &&
    !existingDesksError &&
    !createDesks.isPending &&
    sendableRows.length > 0 &&
    invalidRows.length === 0;

  const toggleDesk = (index: number, value: boolean) => {
    setDeskChecked((prev) => new Map(prev).set(index, value));
  };
  const toggleSubdesk = (key: string, value: boolean) => {
    setUncheckedSubdesks((prev) => {
      const next = new Set(prev);
      if (value) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toSubmission = (row: Row): SuggestedDeskSubmission => {
    const subdesks = selectedSubdesks(row).map((s) => ({
      name: s.subdesk.name,
      description: subdeskDescriptionOf(s),
    }));
    return {
      suggestionName: row.desk.name,
      synced: row.existing !== undefined,
      request: row.existing
        ? syncDeskRequest(row.desk, row.existing, subdesks)
        : newDeskRequest(row.desk, descriptionOf(row), subdesks),
    };
  };

  const handleCreate = async () => {
    setConfirmOpen(false);
    const submitted = sendableRows;
    let result;
    try {
      result = await createDesks.mutateAsync({
        tenantId,
        runId: run.id,
        desks: submitted.map(toSubmission),
      });
    } catch {
      toast.error("Creating the desks failed unexpectedly. Please try again.");
      return;
    }
    // Results come back in the order the desks were submitted. Fully applied
    // rows are deselected; rows with a failure stay selected (a created desk
    // turns into an existing one) so a retry resends only what is missing.
    const isDone = (desk: DeskCreateResult) =>
      desk.outcome !== "failed" &&
      desk.subdesks.every((s) => s.outcome !== "failed");
    setResults((prev) => {
      const next = new Map(prev);
      submitted.forEach((row, i) => {
        // eslint-disable-next-line security/detect-object-injection
        next.set(row.index, result.desks[i]);
      });
      return next;
    });
    setDeskChecked((prev) => {
      const next = new Map(prev);
      submitted.forEach((row, i) => {
        // eslint-disable-next-line security/detect-object-injection
        next.set(row.index, !isDone(result.desks[i]));
      });
      return next;
    });
    setMarkAppliedError(result.markAppliedError);

    const desksCreated = result.desks.filter(
      (d) => d.outcome === "created",
    ).length;
    const subdesks = result.desks.flatMap((d) => d.subdesks);
    const subdesksCreated = subdesks.filter(
      (s) => s.outcome === "created",
    ).length;
    const unexpected = result.desks.filter(
      (d) => d.synced && d.outcome === "created",
    ).length;
    const failures =
      result.desks.filter((d) => d.outcome === "failed").length +
      subdesks.filter((s) => s.outcome === "failed").length;
    const parts: string[] = [];
    if (desksCreated > 0) parts.push(`${plural(desksCreated, "desk")} created`);
    if (subdesksCreated > 0)
      parts.push(`${plural(subdesksCreated, "subdesk")} added`);
    if (failures > 0) parts.push(`${failures} failed`);
    if (unexpected > 0)
      parts.push(
        `${plural(unexpected, "existing desk")} not found and created anew`,
      );
    if (parts.length === 0) parts.push("Everything already existed");
    const message = parts.join(", ");
    if (failures > 0 || unexpected > 0) toast.warning(message);
    else toast.success(message);
  };

  if (rows.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        This run has no desk suggestions.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {existingDesksError && (
        <div
          role="alert"
          className="text-destructive border-destructive/30 rounded-lg border p-3 text-sm"
        >
          Could not load the tenant's existing desks, so desks that already
          exist cannot be shown. Creating is disabled until they load.
        </div>
      )}

      {!runAcceptsCreation && (
        <div className="flex gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Desks can only be created from a completed run.
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <span className="sr-only">Select</span>
            </TableHead>
            <TableHead>Desk</TableHead>
            <TableHead className="min-w-[280px]">Description</TableHead>
            <TableHead className="min-w-[280px]">Subdesks</TableHead>
            <TableHead>Legacy teams</TableHead>
            <TableHead className="text-right">Tickets</TableHead>
            <TableHead>Result</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const { desk, existing, index } = row;
            const result = results.get(index);
            const checked = isChecked(row);
            const description = descriptionOf(row);
            const error =
              isNew(row) && checked
                ? deskValidationError(desk, description)
                : null;
            const errorId = `desk-error-${index}`;
            const missing = missingSubdesks(row).length;
            const syncing = existing !== undefined && !row.createdHere;
            const hintId = `desk-hint-${index}`;
            let hint: {
              text: string;
              tone: "muted" | "warning" | "error";
            } | null = null;
            if (existing && isDisabledDesk(row)) {
              hint = {
                text: "This desk is disabled in the tenant. Enable it before adding subdesks.",
                tone: "warning",
              };
            } else if (existing) {
              if (row.subdesksStatus === "loading") {
                hint = { text: "Checking existing subdesks…", tone: "muted" };
              } else if (row.subdesksStatus === "error") {
                hint = {
                  text: "Could not load this desk's subdesks.",
                  tone: "error",
                };
              } else if (row.createdHere && missing === 0) {
                hint = null;
              } else if (desk.subdesks.length === 0) {
                hint = {
                  text: "No subdesks suggested — nothing to add.",
                  tone: "muted",
                };
              } else if (missing === 0) {
                hint = {
                  text: "All suggested subdesks already exist — nothing to add.",
                  tone: "muted",
                };
              } else if (checked && selectedSubdesks(row).length === 0) {
                hint = {
                  text: "Select at least one new subdesk to add.",
                  tone: "warning",
                };
              }
            }
            return (
              <TableRow
                key={`${desk.name}-${index}`}
                data-testid={`desk-row-${desk.name}`}
              >
                <TableCell className="align-top">
                  <Checkbox
                    aria-label={`Select ${desk.name}`}
                    aria-describedby={hint ? hintId : undefined}
                    checked={checked}
                    disabled={!canSelect(row) || createDesks.isPending}
                    onCheckedChange={(value) => {
                      toggleDesk(index, value === true);
                    }}
                  />
                </TableCell>
                <TableCell className="align-top">
                  <div className="flex items-center gap-2 font-medium">
                    {desk.emoji && <span aria-hidden>{desk.emoji}</span>}
                    {desk.name}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {syncing ? (
                      <Badge variant="secondary">
                        Existing desk – add subdesks
                      </Badge>
                    ) : (
                      <Badge
                        variant="info"
                        className="text-sky-800 dark:text-sky-300"
                      >
                        New desk
                      </Badge>
                    )}
                    {isDisabledDesk(row) && (
                      <Badge variant="warning">Disabled</Badge>
                    )}
                    {desk.template_id && (
                      <Badge variant="outline">Default desk</Badge>
                    )}
                  </div>
                  {syncing && !sameName(existing.name, desk.name) && (
                    <p className="text-muted-foreground mt-1 text-xs">
                      Matches the existing “{existing.name}” desk
                    </p>
                  )}
                  {hint && (
                    <p
                      id={hintId}
                      aria-live="polite"
                      className={
                        hint.tone === "error"
                          ? "text-destructive mt-1 text-xs"
                          : hint.tone === "warning"
                            ? "mt-1 text-xs text-amber-700 dark:text-amber-400"
                            : "text-muted-foreground mt-1 text-xs"
                      }
                    >
                      {hint.text}
                      {row.subdesksStatus === "error" && (
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0 pl-1 text-xs"
                          onClick={row.retrySubdesks}
                        >
                          Retry
                        </Button>
                      )}
                    </p>
                  )}
                </TableCell>
                <TableCell className="align-top">
                  {isNew(row) ? (
                    <>
                      <Textarea
                        aria-label={`Description for ${desk.name}`}
                        value={description}
                        aria-invalid={error !== null || undefined}
                        aria-describedby={error ? errorId : undefined}
                        disabled={createDesks.isPending}
                        onChange={(e) => {
                          const value = e.target.value;
                          setDescriptions((prev) =>
                            new Map(prev).set(index, value),
                          );
                        }}
                      />
                      {error && (
                        <p
                          id={errorId}
                          className="text-destructive mt-1 text-xs"
                        >
                          {error}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      {syncing && (
                        <p className="text-muted-foreground text-xs font-medium">
                          Suggested description (not applied):
                        </p>
                      )}
                      <p className="text-muted-foreground whitespace-pre-wrap text-sm">
                        {description}
                      </p>
                      {syncing && (
                        <p className="text-muted-foreground mt-1 text-xs italic">
                          The existing desk is not changed.
                        </p>
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell className="align-top">
                  {row.subdesks.length > 0 ? (
                    <ul className="space-y-2 text-sm">
                      {row.subdesks.map((subdeskRow) => (
                        <SubdeskItem
                          key={subdeskRow.key}
                          deskName={desk.name}
                          row={subdeskRow}
                          deskChecked={checked}
                          checked={
                            row.subdesksStatus === "ready" &&
                            !subdeskRow.exists &&
                            !uncheckedSubdesks.has(subdeskRow.key)
                          }
                          description={subdeskDescriptionOf(subdeskRow)}
                          disabled={createDesks.isPending}
                          onCheckedChange={(value) => {
                            toggleSubdesk(subdeskRow.key, value);
                          }}
                          onDescriptionChange={(value) => {
                            setSubdeskDescriptions((prev) =>
                              new Map(prev).set(subdeskRow.key, value),
                            );
                          }}
                        />
                      ))}
                    </ul>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="align-top">
                  {desk.legacy_teams.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {desk.legacy_teams.map((team) => (
                        <Badge
                          key={team}
                          variant="outline"
                          className="font-normal"
                        >
                          {team}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right align-top">
                  {desk.ticket_count.toLocaleString()}
                </TableCell>
                <TableCell className="align-top">
                  {result && <ResultCell result={result} />}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {markAppliedError && (
        <p role="alert" className="text-destructive text-sm">
          The changes were saved, but the run could not be marked applied:{" "}
          {markAppliedError}
        </p>
      )}

      <div className="flex justify-end">
        <Button
          data-testid="create-selected"
          disabled={!canCreate}
          onClick={() => {
            setConfirmOpen(true);
          }}
        >
          {createDesks.isPending && (
            <Loader2 aria-hidden className="mr-1.5 h-4 w-4 animate-spin" />
          )}
          {summary || "Nothing selected"}
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{summary}?</AlertDialogTitle>
            <AlertDialogDescription>
              New desks and subdesks are created with no members. Existing desks
              themselves are not changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul
            data-testid="confirm-details"
            className="text-muted-foreground max-h-[50vh] list-disc space-y-1 overflow-y-auto pl-5 text-sm"
          >
            {sendableRows.map((row) => {
              const names = selectedSubdesks(row)
                .map((s) => s.subdesk.name)
                .join(", ");
              return (
                <li key={row.index}>
                  {row.existing ? (
                    <>
                      Add to existing “{row.existing.name}”: {names}
                    </>
                  ) : (
                    <>
                      New desk “{row.desk.name}”{names ? ` with ${names}` : ""}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleCreate();
              }}
            >
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface SubdeskItemProps {
  deskName: string;
  row: SubdeskRow;
  /** Subdesks of an unselected desk are disabled, unchecked and never sent. */
  deskChecked: boolean;
  /** The subdesk's own choice, restored when its desk is selected again. */
  checked: boolean;
  description: string;
  disabled: boolean;
  onCheckedChange: (value: boolean) => void;
  onDescriptionChange: (value: string) => void;
}

function SubdeskItem({
  deskName,
  row,
  deskChecked,
  checked,
  description,
  disabled,
  onCheckedChange,
  onDescriptionChange,
}: SubdeskItemProps) {
  const { subdesk, exists, createdHere, key } = row;
  const editable = deskChecked && checked && !exists;
  const error = editable ? subdeskValidationError(subdesk, description) : null;
  const errorId = `subdesk-error-${key}`;
  return (
    <li data-testid={`subdesk-${deskName}-${subdesk.name}`}>
      <div className="flex items-center gap-2">
        <Checkbox
          aria-label={
            `Select subdesk ${subdesk.name} of ${deskName}` +
            (exists ? " (already exists)" : "")
          }
          checked={deskChecked && checked}
          disabled={exists || !deskChecked || disabled}
          onCheckedChange={(value) => {
            onCheckedChange(value === true);
          }}
        />
        <span className={exists ? "text-muted-foreground" : undefined}>
          {subdesk.name}
        </span>
        {exists &&
          (createdHere ? (
            <Badge variant="positive">created</Badge>
          ) : (
            <Badge variant="secondary">exists</Badge>
          ))}
      </div>
      {editable ? (
        <div className="mt-1 pl-6">
          <Textarea
            aria-label={`Description for subdesk ${subdesk.name} of ${deskName}`}
            className="min-h-10 text-xs md:text-xs"
            value={description}
            placeholder="Optional description"
            aria-invalid={error !== null || undefined}
            aria-describedby={error ? errorId : undefined}
            disabled={disabled}
            onChange={(e) => {
              onDescriptionChange(e.target.value);
            }}
          />
          {error && (
            <p id={errorId} className="text-destructive mt-1 text-xs">
              {error}
            </p>
          )}
        </div>
      ) : (
        description && (
          <div className="text-muted-foreground pl-6 text-xs">
            {description}
          </div>
        )
      )}
    </li>
  );
}
